<template>
  <div>
    <v-fade-transition>
      <v-data-table
        id="clTable"
        :headers="headersByBreakpoint"
        :items="items"
        hide-default-footer
        :hide-default-header="!$vuetify.breakpoint.smAndUp"
        disable-pagination
        @click:row="handleClick"
        class="pointer"
        :group-by="groupBy"
        :sort-by.sync="sortByLocal"
        :item-class="rowClass"
        dense
      >
        <template v-slot:[`group.header`]="{ group, items, isOpen, toggle }">
          <td
            :colspan="headersByBreakpoint.length"
            class="groupHeader"
            :class="{ mobileGroupHeader: !$vuetify.breakpoint.smAndUp }"
            @click="toggle"
          >
            <div
              class="d-flex flex-row align-center headerDiv"
              :class="group > 0 && isOpen ? 'topSpacer' : ''"
            >
              <soft-type-icon
                v-if="groupBy == 'typeOrder'"
                type="type"
                :lens="items[0]"
                :width="24"
                :showLabel="true"
                labelClass="text-h5 mx-3"
                class="ml-4"
              />
              <product-image
                v-if="groupBy == 'companyOrder' || groupBy == 'company'"
                type="companyId"
                :companyId="parseInt(items[0].company_id)"
                :maxHeight="23"
                :maxWidth="150"
                class="my-1"
                :eager="true"
                :placeholder="items[0].company"
                position="left center"
              />
              <v-spacer></v-spacer>
              <v-btn
                text
                icon
                small
                color="primary"
                class="ma-1"
                :ref="group"
                :data-open="isOpen"
                v-if="groupBy"
              >
                <v-icon>{{
                  isOpen ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
            </div>
          </td>
        </template>

        <template v-slot:progress>
          <v-progress-linear
            color="orange"
            :height="10"
            indeterminate
          ></v-progress-linear>
        </template>

        <template v-slot:[`item.image`]="{ item }">
          <div class="imageCell">
            <product-image
              :fileString="item.image"
              type="scl"
              :maxHeight="35"
              :maxWidth="50"
            />
          </div>
        </template>

        <template v-slot:[`item.name`]="{ item }">
          <div class="subtitle-2">{{ item.name }}</div>
          <div
            class="subtleText"
            v-if="
              $vuetify.breakpoint.name == 'sm' &&
              !oneCompany &&
              groupBy != 'company' &&
              groupBy != 'companyOrder'
            "
          >
            {{ item.company }}
          </div>
          <div
            v-if="parseInt(item.discontinued) == 1"
            class="caption red--text"
          >
            (discontinued)
          </div>
        </template>

        <template v-slot:[`item.bc_all`]="{ item }">
          <div class="shortCell">
            {{ removeRange(item.bc_all) | truncate(30) }}
          </div>
        </template>
        <template v-slot:[`item.diam_all`]="{ item }">
          <div class="shortCell">
            {{ removeRange(item.diam_all) | truncate(30) }}
          </div>
        </template>

        <template v-slot:[`item.replace`]="{ item }">
          <!-- <div v-show="false">{{ item.replace }}</div> -->
          <div class="iconCell">
            <soft-type-icon type="replace" :lens="item" :width="25" />
          </div>
        </template>
        <template v-slot:[`item.type`]="{ item }"
          ><div class="iconCell">
            <soft-type-icon type="type" :lens="item" :width="25" />
          </div>
        </template>

        <template v-slot:[`item.dk`]="{ item }">
          {{ item.dk > 0 ? parseInt(item.dk) : "?" }}
        </template>
        <template v-slot:[`item.h2o`]="{ item }">
          {{ item.h2o > 0 ? parseInt(item.h2o) + "%" : "?" }}
        </template>
        <template v-slot:[`item.max_cyl`]="{ item }">
          <span v-if="item.max_cyl && Math.abs(item.max_cyl) > 0">{{
            item.max_cyl
          }}</span>
          <span v-else><v-icon color="#FFAB00">mdi-minus</v-icon></span>
        </template>

        <template v-slot:[`item.max_add`]="{ item }">
          <span v-if="item.max_add && parseFloat(item.max_add) > 0">{{
            "+" + item.max_add
          }}</span>
          <span v-else><v-icon color="#FFAB00">mdi-minus</v-icon></span>
        </template>

        <template v-slot:[`item.max_plus`]="{ item }">
          {{ item.max_plus ? numToDiopterString(item.max_plus, 1) : "plano" }}
        </template>
        <template v-slot:[`item.annualPrice`]="{ item }">
          {{ price(item.annualPrice) }}
        </template>

        <template v-slot:[`item.rx`]="{ item }">
          <b class="noWrap">{{ item.rx }}</b>
          <div v-if="item.bestRx.add" class="ml-2 caption">
            {{ addString(item.bestRx) }} add
          </div>
        </template>

        <template v-slot:[`item.vaString`]="{ item }">
          <div :style="vaStyle(item.va)">{{ item.vaString }}</div>
        </template>

        <!-- the following slots are only used on ipad screens -->
        <template v-slot:[`item.icons`]="{ item }">
          <div class="d-flex">
            <soft-type-icon
              type="replace"
              :lens="item"
              :width="25"
              class="d-flex-inline mx-3"
              :eager="true"
            />

            <soft-type-icon
              type="type"
              :lens="item"
              :width="25"
              class="d-flex-inline mx-3"
              :eager="true"
            />
          </div>
        </template>

        <template v-slot:[`item.sphRange`]="{ item }">
          {{ numToDiopterString(item.max_plus, 1) }} to
          {{ numToDiopterString(item.max_minus, 1) }}
        </template>

        <template v-slot:[`item.oneCell`]="{ item }">
          <div class="d-flex flex-row py-2">
            <div style="width: 55px" class="pr-2 pt-2 d-flex flex-column">
              <product-image
                v-if="item.image"
                :fileString="item.image"
                type="scl"
                :maxWidth="50"
                :maxHeight="30"
                :altText="item.name + ' product image'"
                class="mx-auto"
                position="center center"
              />
              <soft-type-icon
                :lens="item"
                type="replace"
                class="my-2 mx-auto"
                position="center center"
              ></soft-type-icon>
              <soft-type-icon
                :lens="item"
                type="type"
                class="mx-auto"
                position="center center"
              ></soft-type-icon>
            </div>
            <v-divider vertical class="px-1"></v-divider>
            <div class="px-2 pl-2 text-left width100p">
              <div class="title">{{ item.name }}</div>
              <div
                v-if="parseInt(item.discontinued) == 1"
                class="caption ml-4 red--text"
              >
                (discontinued)
              </div>
              <product-image
                v-if="groupBy != 'company' && groupBy != 'companyOrder'"
                type="companyId"
                :companyId="parseInt(item.company_id)"
                :maxWidth="80"
                :maxHeight="50"
                :altText="item.company + ' logo'"
                position="left center"
                classes="mx-0"
                class="pt-1 pb-3"
              />

              <table class="width100p">
                <tr v-if="item.dk">
                  <th>Dk</th>
                  <td>
                    {{ item.dk }}
                  </td>
                </tr>
                <tr v-if="tableType != 'power'">
                  <th>Sph</th>

                  <td>
                    {{ numToDiopterString(item.max_plus, 1) }} to
                    {{ numToDiopterString(item.max_minus, 1) }}
                  </td>
                </tr>
                <tr
                  v-if="
                    item.max_cyl && item.max_cyl != 0 && tableType != 'power'
                  "
                >
                  <th>Cyl</th>
                  <td>to {{ numToDiopterString(item.max_cyl, 1) }}</td>
                </tr>

                <tr
                  v-if="
                    item.max_add && item.max_add > 0 && tableType != 'power'
                  "
                >
                  <th>Add</th>
                  <td>to {{ numToDiopterString(item.max_add, 1) }}</td>
                </tr>
                <tr v-if="item.annualPrice && showAnnualPrices">
                  <th>Price<sup>*</sup></th>
                  <td>{{ item.annualPrice | currency }} / year</td>
                </tr>
                <tr v-if="tableType == 'power' && item.rx && item.bestRx">
                  <th>Rx<sup>*</sup></th>
                  <td>
                    <b>{{ item.bestRx.string }}</b>
                    {{ addString(item.bestRx) }} =
                    <span :style="vaStyle(item.va)">{{ item.vaString }}</span
                    ><sup>*</sup>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </template>

        <!-- end mobile slots -->
      </v-data-table>
    </v-fade-transition>
  </div>
</template>

<script>
// import Name from "../dir/file";
import RxMethods from "@/assets/js/cls/RxMethods";
import ClCompanyMixin from "@/assets/js/cls/ClCompanyMixin";
import StyleUtilities from "@/assets/js/StyleUtilities";
// import HashParserMixin from "@/assets/js/HashParserMixin";

export default {
  name: "SoftClTable",
  mixins: [
    RxMethods,
    ClCompanyMixin,
    StyleUtilities,
    // , HashParserMixin
  ],
  components: {
    ProductImage: () => import("@/components/clStuff/ProductImage"),
    SoftTypeIcon: () => import("@/components/clStuff/SoftTypeIcon"),
  },
  props: {
    items: null,
    groupBy: null, // "company" or "type"
    doExpand: {
      type: Boolean,
      default: true,
    },
    oneCompany: {
      type: Number,
      default: 0,
    },
    showAnnualPrices: { type: Boolean, default: true },
    sortBy: { type: String, default: "name" },
    tableType: { Type: String, default: "default" }, // power
    headers: null, // an optional list of header values. If none provided will use defaultHeaders
    // filterOut: null,
  },
  data: function () {
    return {
      //groupByCompany: false,
      expanded: [],
      togglers: {},
      sortByLocal: "name",
      defaultHeaders: [
        "image",
        "name",
        "company",
        "replace",
        "type",
        "icons",
        "dk",
        "h2o",
        "bc_all",
        "diam_all",

        "max_plus",
        "max_minus",
        "sphRange",
        "max_cyl",
        "max_add",

        "annualPrice",
      ],
      powerHeaders: [
        "image",
        "name",
        "company",
        "replace",
        "type",
        "icons",
        "dk",
        "bc_all",
        "diam_all",
        "rx",
        "vaString",

        "annualPrice",
      ],
      headerOptions: {
        score: { text: "pop", hideOnSm: true },
        image: { text: " ", hideOnSm: true },
        name: {},
        company: { hideOnSm: true },
        replace: { hideOnSm: true },
        type: { hideOnSm: true },
        icons: { text: " ", hideOnMd: 1 },
        bc_all: { text: "BC" },
        diam_all: { text: "Diam" },
        max_plus: { text: "Most+", hideOnSm: true },
        max_minus: { text: "Most-", hideOnSm: true },
        sphRange: { text: "Sphere Range", hideOnMd: true },
        max_cyl: { text: "Toric" },
        max_add: { text: "Add" },
        rx: {},
        vaString: { text: "VA*", showOnSm: 1 },
        dk: {},
        h2o: { text: "H2O", hideOnSm: true },
        annualPrice: { text: "Annual Price*", value: "annualPrice" },
      },
      // hash: "",
    };
  },
  methods: {
    price(price) {
      return price ? "$" + price : "?";
    },
    powerRange(plus, minus) {
      let plusTxt = plus ? this.numToDiopterString(plus, 1) : "";
      let minusTxt = minus ? minus : "";
      let joiner = minusTxt ? " to " : "";
      return plusTxt + joiner + minusTxt;
    },
    handleClick(value) {
      // console.log(value);
      this.$emit("lensSelected", value);
      //this.$router.push({ path: `/cls/details/${value.id}` });
    },
    expand_collapse() {
      this.doExpand ? this.openAll() : this.closeAll();
      // this.doExpand = !this.doExpand;
    },
    closeAll() {
      Object.keys(this.$refs).forEach((k) => {
        // console.log(this.$refs[k]);
        if (this.$refs[k] && this.$refs[k].$attrs["data-open"]) {
          this.$refs[k].$el.click();
        }
      });
    },
    openAll() {
      Object.keys(this.$refs).forEach((k) => {
        if (this.$refs[k] && !this.$refs[k].$attrs["data-open"]) {
          this.$refs[k].$el.click();
        }
      });
    },
    addString(lensRx) {
      // console.log(lensRx);
      let addStr = "";
      if (lensRx.add) {
        addStr =
          " " +
          lensRx.add.charAt(0).toUpperCase() +
          lensRx.add.substr(1).toLowerCase();
        if (lensRx.modifier && lensRx.modifier != "")
          addStr += ` (${lensRx.modifier})`;
      }
      return addStr;
    },
    removeRange(text) {
      // console.log(text);
      if (this.$vuetify.breakpoint.xlAndUp) return text;
      text = text.trim();
      let removed = text.replace(/ in [+-.\d].*$/gim, "");
      removed = removed.replace(
        /^([.0-9]{1,5})[ ,]+[ 0-9,.]*[ ,]+([.0-9]{1,5})$/gim,
        "$1 to $2"
      );
      // console.log("-" + removed);
      return removed;
    },
    rowClass(item) {
      return parseInt(item.discontinued) == 1 ? "pinkRow" : "";
    },
  },
  computed: {
    headersForTable: function () {
      let headers = [];
      if (this.headers) {
        headers = this.headers;
      } else if (this.tableType == "power") {
        headers = this.powerHeaders;
      } else headers = this.defaultHeaders;

      return headers;
    },
    headersByBreakpoint: function () {
      if (this.$vuetify.breakpoint.name == "xs") return [{ value: "oneCell" }];
      let rtn = [];
      let md = this.$vuetify.breakpoint.mdAndUp;
      //don't need to show company (or type) fields if grouped by company (or type)
      let noCompany = this.groupBy == "company";
      let noType = this.groupBy == "type";

      let headerOptions = this.headerOptions;
      let showAnnualPrices = this.showAnnualPrices;

      this.headersForTable.forEach((hKey) => {
        const hObj = headerOptions[hKey];
        let include = true;
        if (hObj.hideOnSm && !md) include = false;
        if (hObj.hideOnMd && md) include = false;
        if (hObj.value == "company" && noCompany) include = false;
        if (hObj.value == "type" && noType) include = false;
        if (hObj.value == "annualPrice" && !showAnnualPrices) include = false;
        // console.log(hObj.value, showAnnualPrices);
        let obj = { value: hKey };
        obj.text = hObj.text
          ? hObj.text
          : hKey.charAt(0).toUpperCase() + hKey.slice(1);
        if (include) rtn.push(obj);
      });

      return rtn;
    },
  },
  watch: {
    doExpand() {
      this.expand_collapse();
    },
    groupBy(val) {
      this.sortByLocal = val;
    },
  },
  mounted: function () {
    this.$emit("loaded", true);
    this.sortByLocal = this.sortBy;
    // let hash = window.location.hash.substring(1);
    // if (hash) this.hash = hash;
  },
};
</script>

<style scoped>
tbody tr:nth-of-type(odd) {
  background-color: rgba(52, 151, 168, 0.05);
}
.nameCell {
  min-width: 110px;
  overflow-wrap: break-word;
}
/* .pointer {
  cursor: pointer;
} */
.imageCell {
  width: 60px;
  padding: 4px 0;
}
.v-data-table >>> .groupHeader {
  background-color: #fff;
  padding: 0 !important;
  min-height: 30px;
}
.v-data-table >>> .mobileGroupHeader {
  display: block;
  width: 99.9%;
  clear: both;
  height: 44px !important;
}
v-data-table >>> .v-data-table__mobile-row__header {
  display: none !important;
}
.v-data-table >>> .v-data-table__mobile-row {
  padding: 0;
  display: block !important;
}
/* .v-data-table >>> .groupHeader {
  background-color: #fff;
  padding: 0 !important;
  min-height: 30px;
} */
.headerDiv {
  padding: 5px;
  border-radius: 3px;
  background-color: #f5fafe;
  border: 1px solid #c7ddf9;
  /* background-color: #ffe6ce; */
  /* border-radius: 50vh; */
  /* margin-bottom: -1px; */
  padding-left: 1.5em;
  min-height: 48px;
}
.topSpacer {
  margin-top: 2em;
}

.shortCell {
  max-width: 90px;
}

.v-data-table >>> .text-start {
  vertical-align: top !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}
.v-data-table >>> .v-row-group__header {
  background-color: transparent !important;
}
.noWrap {
  white-space: nowrap !important;
}
.iconCell {
  width: 30px;
}
#clTable >>> .pinkRow {
  background-color: #ffedf0;
}
</style>
